import { useEffect, useState } from "react";
import crestImg from "@/assets/loader-crest.webp";
import templeImg from "@/assets/loader-member-temple.jpg";

interface AppLoaderProps {
  label?: string;
  fullscreen?: boolean;
  timeoutMs?: number;
  /** "public" = landing/join/sign-in pages, "member" = Kingdom Hub portal */
  variant?: "public" | "member";
}

const Bar = () => (
  <div className="w-56 sm:w-72 h-1.5 rounded-full bg-[hsl(40_45%_88%)] border border-[hsl(40_50%_70%/0.6)] overflow-hidden">
    <div className="h-full w-1/3 rounded-full bg-gradient-to-r from-[hsl(40_60%_45%)] via-[hsl(42_75%_60%)] to-[hsl(40_60%_45%)] animate-[loaderSlide_1.6s_ease-in-out_infinite]" />
    <style>{`@keyframes loaderSlide{0%{transform:translateX(-110%)}100%{transform:translateX(320%)}}`}</style>
  </div>
);

export default function AppLoader({
  label,
  fullscreen = true,
  timeoutMs = 15000,
  variant = "public",
}: AppLoaderProps) {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => setTimedOut(true), timeoutMs);
    return () => window.clearTimeout(id);
  }, [timeoutMs]);

  const shell =
    (fullscreen ? "fixed inset-0 z-50 " : "w-full h-full min-h-[40vh] ") +
    "flex flex-col items-center justify-center text-center px-6 bg-[hsl(40_45%_95%)] overflow-hidden";

  if (timedOut) {
    return (
      <div className={shell} role="alert" aria-live="assertive">
        <img src={crestImg} alt="" className="w-24 h-24 object-contain mb-4" />
        <p className="font-serif text-xl tracking-[0.3em] uppercase text-[hsl(38_55%_38%)]">Decorum Lifestyle</p>
        <p className="mt-4 text-sm text-[hsl(30_20%_30%)] max-w-sm">
          This is taking longer than expected. Check your connection and try again.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="mt-5 px-6 py-2 rounded-full border border-[hsl(40_55%_50%)] text-[hsl(38_55%_35%)] text-xs tracking-[0.3em] uppercase hover:bg-[hsl(40_55%_50%/0.1)]"
        >
          Retry
        </button>
      </div>
    );
  }

  if (variant === "member") {
    return (
      <div className={shell} role="status" aria-live="polite">
        <img src={templeImg} alt="Kingdom Hub temple" className="absolute inset-0 w-full h-full object-contain object-center scale-[1.35] sm:scale-100 sm:object-cover sm:object-center" />
        <div className="relative mt-auto mb-[8vh] flex flex-col items-center gap-3">
          <p className="font-serif italic text-lg sm:text-xl text-[hsl(30_30%_22%)] drop-shadow-[0_1px_2px_hsl(40_50%_95%)]">
            {label ?? "Preparing your quarters…"}
          </p>
          <Bar />
        </div>
      </div>
    );
  }

  return (
    <div className={shell} role="status" aria-live="polite">
      <img src={crestImg} alt="Decorum Lifestyle crest" fetchPriority="high" decoding="sync" className="w-36 h-36 sm:w-44 sm:h-44 object-contain" />
      <p className="mt-6 w-full max-w-full mx-auto px-2 text-center font-serif text-lg sm:text-3xl tracking-[0.15em] sm:tracking-[0.3em] uppercase whitespace-nowrap text-[hsl(38_55%_38%)]">
        Decorum Lifestyle
      </p>
      <div className="mt-6"><Bar /></div>
      <p className="mt-4 font-serif italic tracking-[0.2em] text-[hsl(38_40%_40%)]">{label ?? "Welcome"}</p>
    </div>
  );
}
